import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Alert,
  Typography
} from '@mui/material';
import { Today, AccessTime } from '@mui/icons-material';
import { API_PREFIX } from '../../services/api';

// TimePickerButton Component
const TimePickerButton = ({ label, value, onChange, helperText }) => {
  const [selectedTime, setSelectedTime] = useState(value || '');
  
  const timeOptions = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayTime = new Date(`2000-01-01 ${timeStr}`).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      timeOptions.push({ value: timeStr, label: displayTime });
    }
  }

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
    if (onChange) {
      onChange(newTime);
    }
  };

  return (
    <FormControl fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedTime}
        onChange={(e) => handleTimeChange(e.target.value)}
        label={label}
        startAdornment={<AccessTime sx={{ mr: 1, color: 'action.active' }} />}
      >
        {timeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime fontSize="small" color="primary" />
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

const CreateSingleSlot = ({ open, onClose, panelists, onSlotCreated }) => {
  const [newSlot, setNewSlot] = useState({
    panelist_id: '',
    date: '',
    start_time: '',
    end_time: '',
    notes: '',
    slot_type: 'interview'
  });

  useEffect(() => {
    if (open) {
      setNewSlot({
        panelist_id: '',
        date: '',
        start_time: '',
        end_time: '',
        notes: '',
        slot_type: 'interview'
      });
    }
  }, [open]);

  const combineDateTime = (date, time) => {
    if (!date || !time) return '';
    return new Date(`${date}T${time}:00`).toISOString();
  };

  const handleCreateSlot = async () => {
    try {
      const available_from = combineDateTime(newSlot.date, newSlot.start_time);
      const available_to = combineDateTime(newSlot.date, newSlot.end_time);
      
      if (!available_from || !available_to) {
        // You might want to show an error to the user here
        console.error('Date, start time, and end time are required.');
        return;
      }
      
      const response = await fetch(`${API_PREFIX}/admin/expert-slots`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          panelist_id: newSlot.panelist_id,
          available_from: available_from,
          available_to: available_to,
          is_recurring: false,
          notes: newSlot.notes,
          slot_type: newSlot.slot_type
        })
      });

      if (response.ok) {
        const result = await response.json();
        onSlotCreated(result);
        onClose();
      } else {
        const errorData = await response.json();
        console.error('Slot creation error:', errorData);
        // You might want to show an error to the user here
      }
    } catch (error) {
      console.error('Network error:', error);
      // You might want to show an error to the user here
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Today color="primary" />
          Create Single Slot
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Panelist</InputLabel>
            <Select
              value={newSlot.panelist_id}
              onChange={(e) => setNewSlot({ ...newSlot, panelist_id: e.target.value })}
              label="Select Panelist"
            >
              {panelists.map((panelist) => (
                <MenuItem key={panelist.id} value={panelist.id}>
                  <Box>
                    <Typography variant="subtitle2">{panelist.username}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {panelist.username}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Date"
            type="date"
            value={newSlot.date}
            onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            helperText="Select the specific date for this slot"
          />
          
          <Stack direction="row" spacing={2}>
            <TimePickerButton
              label="Start Time"
              value={newSlot.start_time}
              onChange={(time) => setNewSlot({ ...newSlot, start_time: time })}
              helperText="e.g., 3:00 PM"
            />
            
            <TimePickerButton
              label="End Time"
              value={newSlot.end_time}
              onChange={(time) => setNewSlot({ ...newSlot, end_time: time })}
              helperText="e.g., 4:00 PM"
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>Slot Type</InputLabel>
            <Select
              value={newSlot.slot_type}
              onChange={(e) => setNewSlot({ ...newSlot, slot_type: e.target.value })}
              label="Slot Type"
            >
              <MenuItem value="interview">Interview</MenuItem>
              <MenuItem value="cv_review">CV Review</MenuItem>
              <MenuItem value="feedback">Feedback Session</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            multiline
            rows={2}
            value={newSlot.notes}
            onChange={(e) => setNewSlot({ ...newSlot, notes: e.target.value })}
            fullWidth
            placeholder="Optional notes for this slot..."
          />

          {newSlot.start_time && newSlot.end_time && newSlot.date && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Slot Summary:</strong> {new Date(newSlot.date).toLocaleDateString()} at {new Date(`2000-01-01 ${newSlot.start_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})} - {new Date(`2000-01-01 ${newSlot.end_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})}
                <br />
                <strong>Duration:</strong> {Math.round((new Date(`2000-01-01 ${newSlot.end_time}`) - new Date(`2000-01-01 ${newSlot.start_time}`)) / (1000 * 60))} minutes
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleCreateSlot} 
          variant="contained" 
          startIcon={<Today />}
          disabled={!newSlot.panelist_id || !newSlot.date || !newSlot.start_time || !newSlot.end_time}
        >
          Create Single Slot
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSingleSlot;
